import React from "react"
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import { Github, Book, Share2, Server } from "lucide-react"
import SEO from './common/SEO'

interface ProjectCardProps {
  title: string;
  description: string;
  githubUrl: string;
  techStack: string[];
  license: string;
  stars: number;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ 
  title, 
  description, 
  githubUrl, 
  techStack, 
  license, 
  stars 
}) => {
  return (
    <div className="bg-white rounded-3xl p-8 shadow-lg">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <a 
            href={githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-2xl font-semibold hover:text-[#1F80FF] transition-colors"
          >
            {title}
          </a>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-muted-foreground">
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 .25a.75.75 0 01.673.418l1.882 3.815 4.21.612a.75.75 0 01.416 1.279l-3.046 2.97.719 4.192a.75.75 0 01-1.088.791L8 12.347l-3.766 1.98a.75.75 0 01-1.088-.79l.72-4.194L.818 6.374a.75.75 0 01.416-1.28l4.21-.611L7.327.668A.75.75 0 018 .25z"/>
              </svg>
              <span>{stars}</span>
            </div>
            <a 
              href={githubUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-[#1F80FF] transition-colors"
            >
              <Github className="w-6 h-6" />
            </a>
          </div>
        </div>
        <p className="text-muted-foreground text-lg">
          {description}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#1F80FF]/10 text-[#1F80FF] px-3 py-1 rounded-full text-sm">
            {license}
          </span>
          {techStack.map((tech, index) => (
            <span 
              key={index}
              className="bg-[#1F80FF]/10 text-[#1F80FF] px-3 py-1 rounded-full text-sm"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

const ResearchPage: React.FC = () => {
  const projects: ProjectCardProps[] = [
    {
      title: "LibDC-Swift",
      description: `A Swift framework for communicating with dive computers via Bluetooth Low Energy (BLE). Built on top of libdivecomputer, this package provides modern Swift bindings for iOS applications. Features include BLE device scanning, support for Suunto and Shearwater dive computers, efficient dive log retrieval with fingerprint system, and comprehensive dive data parsing.`,
      githubUrl: "https://github.com/deepsealabs/libdc-swift",
      techStack: ["Swift", "BLE", "libdivecomputer", "iOS", "macOS"],
      license: "LGPL-2.1",
      stars: 5
    },
    {
      title: "FIT Parser Swift",
      description: `A Swift package for parsing and displaying dive data from FIT files. Supporting FIT 21.141.0 protocol, it provides structures for organizing dive data including sessions, summaries, settings, and tank information. Features a SwiftUI interface for displaying parsed data and a command-line tool for quick parsing.`,
      githubUrl: "https://github.com/deepsealabs/fit-parser-swift",
      techStack: ["Swift", "FIT Protocol", "SwiftUI", "XML"],
      license: "MIT",
      stars: 1
    }
  ];

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/research" />
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="container flex flex-col items-center justify-center space-y-4 pt-32 text-center">
          <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl">
            Open Source &<br />
            <span className="text-[#1F80FF]">Community Contributions</span>
          </h1>
          <p className="max-w-[700px] text-xl text-muted-foreground">
            Nothing lives forever - not even apps. But open source contributions? They stick around. 
            That&apos;s why we&apos;re committed to sharing our core technology with the diving community, 
            ensuring that our work continues to benefit divers long into the future.
          </p>
        </section>

        {/* Philosophy Section */}
        <section className="container py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-[#1F80FF]/10 rounded-xl flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Open Source First</h3>
              <p className="text-muted-foreground">
                We believe in open collaboration. Our core libraries for dive computer communication 
                and data parsing are freely available to the community.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-[#1F80FF]/10 rounded-xl flex items-center justify-center mb-4">
                <Book className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Clear Documentation</h3>
              <p className="text-muted-foreground">
                Comprehensive documentation and examples help developers understand and build upon our work, 
                fostering innovation in dive technology.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-[#1F80FF]/10 rounded-xl flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Sustainable Development</h3>
              <p className="text-muted-foreground">
                While our core technology is open source, we maintain cloud infrastructure and premium features 
                to ensure continuous development and support.
              </p>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="container py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">Our Open Source Projects</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Core technologies that power Currents, available for the community
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-[1200px] mx-auto">
            {projects.map((project, index) => (
              <ProjectCard key={index} {...project} />
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="container py-20 text-center">
          <div className="max-w-[800px] mx-auto space-y-8">
            <h2 className="text-3xl font-bold">Want to Contribute?</h2>
            <p className="text-lg text-muted-foreground">
              We welcome contributions from the diving and developer community. Check out our GitHub 
              repositories or get in touch to learn more about how you can help shape the future of dive technology.
            </p>
            <div className="flex justify-center">
              <a 
                href="https://github.com/deepsealabs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-[#1F80FF] text-white px-6 py-3 rounded-full hover:bg-[#1F80FF]/90"
              >
                <Github className="w-5 h-5" />
                <span>View Our GitHub</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <Analytics />
    </div>
  )
}

export default ResearchPage